import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LazyMotion, MotionConfig, domAnimation } from "framer-motion";
import { LanguageProvider, PREFIXED_LANGS, useLang } from "@/i18n";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import UnitPage from "@/pages/UnitPage";
import UnitsPage from "@/pages/UnitsPage";
import Dintorni from "@/pages/Dintorni";
import Contatti from "@/pages/Contatti";
import DoveSiamo from "@/pages/DoveSiamo";
import NotFound from "@/pages/NotFound";
import { UNITS } from "@/data/site";

// The booking flow carries the calendar and date locales; most visitors never
// open it, so it loads on demand.
const PrenotaUnit = lazy(() => import("@/pages/PrenotaUnit"));
const Prenota = lazy(() => import("@/pages/Prenota"));
const Privacy = lazy(() => import("@/pages/Privacy"));

const SkipLink = () => {
  const { t } = useLang();
  return (
    <a
      href="#main"
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:rounded-full focus:bg-ink focus:px-5 focus:py-3 focus:text-sm focus:text-cream"
    >
      {t.nav.skip}
    </a>
  );
};

const ScrollManager = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
        return;
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash]);
  return null;
};

// Un'unica fonte di verità per le route: mappata 3 volte in <Routes>
// sotto (nessun prefisso = it, /en/*, /fr/*), stessi slug ovunque.
const ROUTE_DEFS = [
  { path: "/", element: <Home /> },
  ...UNITS.flatMap((u) => [
    { path: `/${u.slug}`, element: <UnitPage slug={u.slug} /> },
    { path: `/prenota/${u.slug}`, element: <PrenotaUnit slug={u.slug} /> },
  ]),
  { path: "/prenota", element: <Prenota /> },
  { path: "/dintorni", element: <Dintorni /> },
  { path: "/unita", element: <UnitsPage /> },
  { path: "/contatti", element: <Contatti /> },
  { path: "/dove-siamo", element: <DoveSiamo /> },
  { path: "/privacy", element: <Privacy doc="privacy" /> },
  { path: "/cookie", element: <Privacy doc="cookies" /> },
];

function App() {
  return (
    <div className="min-h-screen">
      <div className="noise-overlay" aria-hidden="true" />
      <LazyMotion features={domAnimation} strict>
      <MotionConfig reducedMotion="user">
      <BrowserRouter>
        <LanguageProvider>
          <SkipLink />
          <ScrollManager />
          <Navbar />
          <Suspense fallback={<main id="main" tabIndex={-1} className="min-h-screen outline-none" />}>
            <main id="main" tabIndex={-1} className="outline-none">
              <Routes>
                {ROUTE_DEFS.map((r) => (
                  <Route key={`it${r.path}`} path={r.path} element={r.element} />
                ))}
                {PREFIXED_LANGS.map((l) =>
                  ROUTE_DEFS.map((r) => (
                    <Route
                      key={`${l}${r.path}`}
                      path={`/${l}${r.path === "/" ? "" : r.path}`}
                      element={r.element}
                    />
                  ))
                )}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <Footer />
          </Suspense>
        </LanguageProvider>
      </BrowserRouter>
      </MotionConfig>
      </LazyMotion>
    </div>
  );
}

export default App;
